import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

function BookingPage() {
  const { spaceId } = useParams();
  const navigate = useNavigate();
  const [space, setSpace] = useState(null);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [licensePlate, setLicensePlate] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [favorite, setFavorite] = useState(false);

  useEffect(() => {
    const fetchSpace = async () => {
      try {
        const res = await axios.get(`/api/parking/${spaceId}`);
        setSpace(res.data);
      } catch (err) {
        setMessage('Error fetching parking space.');
      }
    };
    fetchSpace();
  }, [spaceId]);

  const handleBooking = async () => {
    if (!startTime || !endTime || !licensePlate || !phoneNumber) {
      setMessage('Please fill in all fields.');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        '/api/bookings/create',
        {
          space_id: spaceId,
          start_time: startTime,
          end_time: endTime,
          license_plate: licensePlate,
          phone_number: phoneNumber
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setMessage(res.data.message);
    } catch (err) {
      setMessage(err.response?.data?.message || 'Error creating booking.');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleFavorite = () => setFavorite(!favorite);
  const googleMapsLink = space ? `https://www.google.com/maps/dir/?api=1&destination=${space.lat},${space.lng}` : '#';

  if (!space) return <div className="p-6 text-center">Loading...</div>;

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <h2 className="text-2xl font-bold">Book Parking Space</h2>

      <div className="border rounded p-4 shadow space-y-3">
        <MapContainer
          center={[space.lat, space.lng]}
          zoom={16}
          scrollWheelZoom={false}
          className="h-64 w-full rounded"
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution="&copy; OpenStreetMap contributors"
          />
          <Marker position={[space.lat, space.lng]}>
            <Popup>{space.address}</Popup>
          </Marker>
        </MapContainer>

        <h3 className="text-xl font-semibold">{space.address}</h3>
        <p className="text-sm text-gray-600">{space.description}</p>
        <div className="my-2">
          ⭐ 4.5 / 5 · <a href="#" className="text-blue-600">Read reviews</a>
        </div>
        <p><strong>Hours:</strong> 24/7</p>
        <p><strong>Contact:</strong> (555) 123-4567</p>
        <p><strong>Email:</strong> owner@example.com</p>
        <p><strong>Price:</strong> ${space.price_per_hour}/hr</p>
        <p><strong>Capacity:</strong> {space.space_capacity}</p>
        <div className="space-x-4 mt-2">
          <a href={googleMapsLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Get Directions</a>
          <button onClick={toggleFavorite} className="text-blue-600 underline">
            {favorite ? '★ Favorited' : '☆ Favorite'}
          </button>
        </div>
      </div>

      <div className="space-y-4">
        <label className="block font-semibold">Start Time</label>
        <input type="datetime-local" value={startTime} onChange={(e) => setStartTime(e.target.value)}
          className="w-full border p-2 rounded" />

        <label className="block font-semibold">End Time</label>
        <input type="datetime-local" value={endTime} onChange={(e) => setEndTime(e.target.value)}
          className="w-full border p-2 rounded" />

        <label className="block font-semibold">License Plate</label>
        <input type="text" value={licensePlate} onChange={(e) => setLicensePlate(e.target.value)}
          className="w-full border p-2 rounded" />

        <label className="block font-semibold">Phone Number</label>
        <input type="text" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)}
          className="w-full border p-2 rounded" />

        <button
          onClick={handleBooking}
          className="bg-green-600 text-white px-4 py-2 rounded"
          disabled={submitting}
        >
          {submitting ? 'Booking...' : 'Book Now'}
        </button>

        {message && <p className="mt-4 text-center font-medium">{message}</p>}
      </div>
    </div>
  );
}

export default BookingPage;
